/**
 * CSV File Utilities
 * Shared utility functions for CSV file operations
 */

import {
  ACCEPTED_FORMATS,
  ACCEPTED_MIME_TYPES,
  MAX_FILE_SIZE,
  MAX_FILE_SIZE_TEXT,
  SUPPORTED_FORMATS_TEXT,
} from './constants';

/**
 * Formats file size to display in KB or MB
 * @param {number} bytes - File size in bytes
 * @returns {string} Formatted file size (e.g., "5.00 KB" or "4.50 MB")
 */
export const formatFileSize = (bytes) => {
  const mb = bytes / 1024 / 1024;
  if (mb < 1) {
    const kb = bytes / 1024;
    return `${kb.toFixed(2)} KB`;
  }
  return `${mb.toFixed(2)} MB`;
};

/**
 * Validates file format and size
 * @param {File} file - File to validate
 * @returns {{ isValid: boolean; error?: string }} Validation result
 */
export const validateCSVFile = (file) => {
  // Check file size
  if (file.size > MAX_FILE_SIZE) {
    return {
      isValid: false,
      error: `File size exceeds ${MAX_FILE_SIZE_TEXT}. Please upload a smaller file.`,
    };
  }

  // Check file extension
  const fileName = file.name.toLowerCase();
  const hasValidExtension = ACCEPTED_FORMATS.some((format) =>
    fileName.endsWith(format),
  );

  // Check MIME type (fallback)
  const hasValidMimeType =
    ACCEPTED_MIME_TYPES.includes(file.type) || file.type === '';

  if (!hasValidExtension && !hasValidMimeType) {
    return {
      isValid: false,
      error: `Invalid file format. Please upload a ${SUPPORTED_FORMATS_TEXT} file.`,
    };
  }

  return { isValid: true };
};

/**
 * Gets the accept attribute value for file input
 * @returns {string} Accept attribute value
 */
export const getFileInputAccept = () => {
  return ACCEPTED_FORMATS.join(',');
};
